import { useState, useEffect } from 'react';
import { Layout, Menu, Dropdown, Avatar, message, Modal, Form, Input } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  SettingOutlined,
  LogoutOutlined,
  ApiOutlined,
  TeamOutlined,
  ControlOutlined,
  CreditCardOutlined,
  GiftOutlined,
  MessageOutlined,
  CustomerServiceOutlined,
  KeyOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  LockOutlined,
  RocketOutlined,
  AppstoreOutlined,
  FolderOutlined,
  SwapOutlined,
  ToolOutlined,
  HeartOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { t, getLang, setLang } from '../utils/i18n';

const { Header, Sider } = Layout;

// API 基础地址
const API_BASE = process.env.REACT_APP_API_URL || '';

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSiderVisible, setMobileSiderVisible] = useState(false);
  const [lang, setLangState] = useState(getLang());
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // 监听语言变化，触发重新渲染
    const handleStorageChange = () => {
      setLangState(getLang());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    setLangState(newLang);
  };

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: t('dashboard'),
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: t('users'),
    },
    {
      key: '/orders',
      icon: <ShoppingCartOutlined />,
      label: t('orders'),
    },
    {
      key: '/usage-dashboard',
      icon: <BarChartOutlined />,
      label: t('usage_statistics'),
    },
    {
      key: '/api-keys',
      icon: <KeyOutlined />,
      label: t('api_keys'),
    },
    {
      key: '/providers',
      icon: <ApiOutlined />,
      label: t('providers'),
    },
    {
      key: '/affiliates',
      icon: <TeamOutlined />,
      label: t('affiliates'),
    },
    {
      key: '/referral-campaigns',
      icon: <GiftOutlined />,
      label: t('activity_center'),
    },
    {
      key: '/feedbacks',
      icon: <MessageOutlined />,
      label: t('feedbacks'),
    },
    {
      key: '/customer-service-config',
      icon: <CustomerServiceOutlined />,
      label: t('customer_service_config'),
    },
    {
      key: '/sms-config',
      icon: <MessageOutlined />,
      label: t('sms_config'),
    },
    {
      key: '/system-settings',
      icon: <ControlOutlined />,
      label: t('system_settings'),
    },
    {
      key: '/payment-settings',
      icon: <CreditCardOutlined />,
      label: t('payment_settings'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: t('settings'),
    },
    {
      key: 'system-operation',
      icon: <RocketOutlined />,
      label: t('laboratory'),
      children: [
        {
          key: '/system-maintenance',
          icon: <SafetyCertificateOutlined />,
          label: t('system_maintenance'),
        },
        {
          key: '/plugins',
          icon: <RocketOutlined />,
          label: t('plugins'),
        },
        {
          key: '/presets',
          icon: <AppstoreOutlined />,
          label: t('presets'),
        },
        {
          key: '/batches',
          icon: <FolderOutlined />,
          label: t('batch_tasks'),
        },
        {
          key: '/rate-limits',
          icon: <SafetyCertificateOutlined />,
          label: t('rate_limit_management'),
        },
        {
          key: '/route-router',
          icon: <SwapOutlined />,
          label: t('route_router'),
        },
        {
          key: '/provider-health',
          icon: <HeartOutlined />,
          label: t('health_monitor'),
        },
        {
          key: 'channel-management',
          icon: <ToolOutlined />,
          label: t('ai_qudao'),
          children: [
            {
              key: '/openrouter-models',
              icon: <ApiOutlined />,
              label: 'OpenRouter',
            },
            {
              key: '/xrtoken',
              icon: <ApiOutlined />,
              label: 'XRToken',
            },
            {
              key: '/w3-models',
              icon: <ApiOutlined />,
              label: 'W系模型',
            },
            {
              key: '/wan3-models',
              icon: <ApiOutlined />,
              label: 'WAN3.0 模型',
            },
            {
              key: '/qiwise',
              icon: <ApiOutlined />,
              label: 'Qiwise',
            },
          ],
        },
      ],
    },
  ];

  const handleMenuClick = (e) => {
    navigate(e.key);
    // 移动端点击菜单后关闭侧边栏
    if (window.innerWidth < 768) {
      setMobileSiderVisible(false);
      document.body.classList.remove('sider-mobile-open');
    }
  };

  const toggleMobileSider = () => {
    setMobileSiderVisible(!mobileSiderVisible);
    // 移动端展开菜单时禁止背景滚动
    if (!mobileSiderVisible) {
      document.body.classList.add('sider-mobile-open');
    } else {
      document.body.classList.remove('sider-mobile-open');
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await passwordForm.validateFields();
      setPasswordLoading(true);

      const response = await fetch(`${API_BASE}/api/admin/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify({
          old_password: values.old_password,
          new_password: values.new_password,
        }),
      });

      const data = await response.json();
      if (data.code === 0) {
        message.success('密码修改成功');
        setPasswordModalVisible(false);
        passwordForm.resetFields();
      } else {
        message.error(data.message || '密码修改失败');
      }
    } catch (error) {
      message.error('密码修改失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogout = () => {
    console.log('=== 执行退出登录 ===');
    localStorage.removeItem('admin_token');
    message.success('已退出登录');
    console.log('Token 已删除');
    console.log('准备跳转到登录页...');
    // 使用 window.location.href 强制跳转
    window.location.href = '/login';
  };

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('logout'),
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'change-password') {
      setPasswordModalVisible(true);
    }
  };

  return (
    <Layout className="admin-layout">
      {/* 移动端遮罩层 */}
      <div
        className={`admin-sider-mask ${mobileSiderVisible ? 'mobile-show' : ''}`}
        onClick={() => {
          setMobileSiderVisible(false);
          document.body.classList.remove('sider-mobile-open');
        }}
      />
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="lg"
        trigger={null}
        theme="dark"
        width={200}
        collapsedWidth={80}
        className={mobileSiderVisible ? 'mobile-show' : ''}
      >
        <div className="admin-sider-logo">
          <img src="/logo.png" alt="Blue API" />
          {!collapsed && <span>Blue API</span>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <div className={`admin-content-wrapper ${collapsed ? 'admin-sider-collapsed' : ''}`}>
        <Header className="admin-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span
              onClick={() => {
                if (window.innerWidth < 768) {
                  toggleMobileSider();
                } else {
                  setCollapsed(!collapsed);
                }
              }}
              style={{
                cursor: 'pointer',
                fontSize: 16,
                color: '#6b7280',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
          </div>
          <div className="admin-header-right">
            <div className="admin-lang-switch">
              <div className={`lang-slider ${lang === 'en' ? 'en' : ''}`}></div>
              <span className={lang === 'zh' ? 'active' : ''} onClick={() => handleLangChange('zh')}>中</span>
              <span className={lang === 'en' ? 'active' : ''} onClick={() => handleLangChange('en')}>EN</span>
            </div>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div className="admin-user-info">
                <Avatar icon={<UserOutlined />} size={32} style={{ backgroundColor: '#1677ff' }} />
                <span style={{ marginLeft: 8, fontWeight: 500 }}>{t('admin')}</span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <div className="admin-content">
          <Outlet />
        </div>
      </div>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onOk={handleChangePassword}
        onCancel={() => {
          setPasswordModalVisible(false);
          passwordForm.resetFields();
        }}
        confirmLoading={passwordLoading}
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={passwordForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="old_password"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password placeholder="请输入当前密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码长度至少8位' },
            ]}
          >
            <Input.Password placeholder="请输入新密码（至少8位）" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="请再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;
